import React, { useMemo, useState } from "react";
import {
  FiDownload,
  FiInfo,
  FiMoreVertical,
  FiPlus,
} from "react-icons/fi";
import { FaArrowRight } from "react-icons/fa6";
import {
  LuAlertCircle,
  LuBarChart3,
  LuCheck,
  LuCreditCard,
  LuFileText,
  LuTrendingUp,
  LuUsers,
} from "react-icons/lu";
import PayoutDataTable from "./PayoutDataTable";
import PayoutDateRangeFilter from "./PayoutDateRangeFilter";
import PayoutDropdown from "./PayoutDropdown";
import PayoutFilterActions from "./PayoutFilterActions";
import PayoutFilterChips from "./PayoutFilterChips";
import PayoutKpiValue from "./PayoutKpiValue";
import PayoutSearchFilter from "./PayoutSearchFilter";
import PayoutTableCell from "./PayoutTableCell";
import { showPayoutNotification } from "./PayoutNotification";
import {
  formatPayoutDateRangeLabel,
  type PayoutDateRangeValue,
} from "./payoutDateUtils";
import { apiNumber, apiString, reportFromApiRecord } from "./api/payoutApiAdapters";
import {
  createPayoutIdempotencyKey,
  isPayoutApiMode,
  payoutApi,
  toRupeesFromWire,
} from "./api/payoutApiClient";
import { usePayoutResource } from "./api/usePayoutResource";

const insightStats = [
  {
    label: "Total Payouts",
    value: 0,
    helper: "From payouts-service",
    icon: <LuTrendingUp />,
    tone: "bg-[#6687FF1A] text-[#6687FF]",
    valueType: "currency",
  },
  {
    label: "Total Transactions",
    value: "0",
    helper: "From payouts-service",
    icon: <LuCreditCard />,
    tone: "bg-[#6687FF1A] text-[#6687FF]",
  },
  {
    label: "Success Rate",
    value: "0%",
    helper: "From payouts-service",
    icon: <LuCheck />,
    tone: "bg-[#DCFCE7] text-[#16A34A]",
  },
  {
    label: "Avg. Payout Amount",
    value: 0,
    helper: "From payouts-service",
    icon: <LuUsers />,
    tone: "bg-[#EDE9FE] text-[#6D28D9]",
    valueType: "currency",
  },
  {
    label: "Failed Payouts",
    value: "0",
    helper: "From payouts-service",
    icon: <LuAlertCircle />,
    tone: "bg-[#FEE2E2] text-[#EF4444]",
    negative: true,
  },
];

const payoutTrend: Array<{ label: string; value: number }> = [];

const statusBreakdown = [
  { label: "Successful", value: 0, percent: "0%", color: "#5FBD74" },
  { label: "Pending", value: 0, percent: "0%", color: "#E5A642" },
  { label: "Failed", value: 0, percent: "0%", color: "#D66A5F" },
];

const methodBreakdown = [
  { label: "NEFT", value: "0", percent: "0%", color: "#8A67C8" },
  { label: "IMPS", value: "0", percent: "0%", color: "#5A8DEE" },
  { label: "RTGS", value: "0", percent: "0%", color: "#E47749" },
  { label: "UPI", value: "0", percent: "0%", color: "#61B7C4" },
  { label: "Others", value: "0", percent: "0%", color: "#A9B2CF" },
];

const topBeneficiaries: string[][] = [];

const failureAnalysis: string[][] = [];

const reportStats = [
  {
    label: "Total Payouts",
    value: 0,
    helper: "From payouts-service",
    icon: <LuTrendingUp />,
    tone: "bg-[#6687FF1A] text-[#6687FF]",
    valueType: "currency",
  },
  {
    label: "Total Transactions",
    value: "0",
    helper: "From payouts-service",
    icon: <LuCreditCard />,
    tone: "bg-[#6687FF1A] text-[#6687FF]",
  },
  {
    label: "Success Rate",
    value: "0%",
    helper: "From payouts-service",
    icon: <LuCheck />,
    tone: "bg-[#DCFCE7] text-[#16A34A]",
  },
  {
    label: "Total Fees & Charges",
    value: 0,
    helper: "From payouts-service",
    icon: <LuUsers />,
    tone: "bg-[#EDE9FE] text-[#6D28D9]",
    valueType: "currency",
  },
  {
    label: "Failed Payouts",
    value: "0",
    helper: "From payouts-service",
    icon: <LuAlertCircle />,
    tone: "bg-[#FEE2E2] text-[#EF4444]",
    negative: true,
  },
];

const reportTemplates = [
  {
    name: "Payout Summary Report",
    description: "Summary of all payouts with total amount, count and success rate.",
    category: "Summary",
    frequency: "Daily",
    account: "All Accounts",
    method: "All Payout Methods",
    status: "Active",
    generatedAt: "-",
    generatedBy: "-",
    tone: "bg-[#DBEAFE] text-[#2563EB]",
    icon: <LuBarChart3 />,
  },
  {
    name: "Payout Transaction Report",
    description: "Detailed report of all payout transactions.",
    category: "Transaction",
    frequency: "Daily",
    account: "Operating Account",
    method: "NEFT",
    status: "Active",
    generatedAt: "-",
    generatedBy: "-",
    tone: "bg-[#EDE9FE] text-[#6D28D9]",
    icon: <LuFileText />,
  },
  {
    name: "Failed Payouts Report",
    description: "List of failed payouts with reason and status.",
    category: "Exception",
    frequency: "Daily",
    account: "All Accounts",
    method: "All Payout Methods",
    status: "Failed",
    generatedAt: "-",
    generatedBy: "-",
    tone: "bg-[#FEE2E2] text-[#EF4444]",
    icon: <LuAlertCircle />,
  },
  {
    name: "Beneficiary Report",
    description: "List of all beneficiaries with account details.",
    category: "Master Data",
    frequency: "Weekly",
    account: "Vendor Account",
    method: "NEFT",
    status: "Active",
    generatedAt: "-",
    generatedBy: "-",
    tone: "bg-[#FEF3C7] text-[#D97706]",
    icon: <LuUsers />,
  },
  {
    name: "Payout Settlement Report",
    description: "Payouts settled to bank and settlement timeline.",
    category: "Settlement",
    frequency: "Daily",
    account: "Salary Account",
    method: "IMPS",
    status: "Active",
    generatedAt: "-",
    generatedBy: "-",
    tone: "bg-[#CCFBF1] text-[#0F766E]",
    icon: <LuFileText />,
  },
  {
    name: "Fees & Charges Report",
    description: "Detailed breakup of fees and charges.",
    category: "Finance",
    frequency: "Monthly",
    account: "All Accounts",
    method: "RTGS",
    status: "Active",
    generatedAt: "-",
    generatedBy: "-",
    tone: "bg-[#FEF3C7] text-[#D97706]",
    icon: <LuCreditCard />,
  },
];

const reportRows: Array<(typeof reportTemplates)[number] & { id: string }> = [];

type ReportRow = (typeof reportRows)[number] & {
  reportType?: string;
  jobId?: string;
  downloadUrl?: string;
};
type ReportMenuState = {
  row: ReportRow;
  top: number;
  left: number;
};

const apiFallbackReportRows: ReportRow[] = [];

const emptyReportRow: ReportRow = {
  id: "",
  name: "",
  description: "",
  category: "",
  frequency: "",
  account: "",
  method: "",
  status: "",
  generatedAt: "",
  generatedBy: "",
  tone: "bg-[#EEF2FF] text-[#4F46E5]",
  icon: <LuFileText />,
};

const inferReportType = (row: Partial<ReportRow>) => {
  const text = `${row.reportType || ""} ${row.name || ""} ${row.category || ""}`.toLowerCase();
  if (text.includes("transaction")) return "PAYOUT_TRANSACTIONS";
  if (text.includes("failed") || text.includes("exception")) return "FAILED_PAYOUTS";
  if (text.includes("beneficiar")) return "BENEFICIARIES";
  if (text.includes("settlement")) return "SETTLEMENT";
  if (text.includes("fee") || text.includes("charge") || text.includes("finance")) return "FEES_CHARGES";
  return "PAYOUT_SUMMARY";
};

const reportFormatForApi = (format: string) =>
  format.toUpperCase() === "XLS" ? "XLSX" : format.toUpperCase();

const toApiDate = (date: Date) => date.toISOString().slice(0, 10);

const reportRowFromApiRecord = (record: Record<string, unknown>, index: number): ReportRow => {
  const fallback = apiFallbackReportRows[index % apiFallbackReportRows.length] || emptyReportRow;
  const adapted = reportFromApiRecord(record, fallback as unknown as Record<string, unknown>);
  const row = {
    ...fallback,
    ...adapted,
    id: apiString(record, ["job_id", "jobId", "id", "report_id", "reportId"], fallback.id),
    jobId: apiString(record, ["job_id", "jobId", "id"], ""),
    reportType: apiString(
      record,
      ["report_type", "reportType", "template_type", "templateType", "type", "key"],
      inferReportType(adapted as Partial<ReportRow>),
    ),
    downloadUrl: apiString(record, ["download_url", "downloadUrl", "url"], ""),
  };
  return row as ReportRow;
};

const buildReportJobBody = (
  row: Partial<ReportRow>,
  format: string,
  filters: {
    account: string;
    method: string;
    status: string;
    dateRange?: PayoutDateRangeValue | null;
  },
) => ({
  report_type: inferReportType(row),
  from: filters.dateRange ? toApiDate(filters.dateRange.startDate) : "2025-05-20",
  to: filters.dateRange ? toApiDate(filters.dateRange.endDate) : "2025-05-20",
  format: reportFormatForApi(format),
  process_now: true,
  filters: {
    ...(filters.account !== "All Accounts" ? { account: filters.account } : {}),
    ...(filters.method !== "All Payout Methods" ? { mode: filters.method } : {}),
    ...(filters.status !== "All Status" ? { status: filters.status } : {}),
  },
});

const readReportDownloadUrl = (record: Record<string, unknown>) => {
  const nestedData = record.data && typeof record.data === "object"
    ? (record.data as Record<string, unknown>)
    : undefined;
  return (
    apiString(record, ["download_url", "downloadUrl", "url", "signed_url", "signedUrl"], "") ||
    (nestedData
      ? apiString(nestedData, ["download_url", "downloadUrl", "url", "signed_url", "signedUrl"], "")
      : "")
  );
};

const readReportSummaryMetric = (
  summary: Record<string, unknown>,
  paiseKeys: string[],
  valueKeys: string[],
  fallback: string | number,
) => {
  for (const key of paiseKeys) {
    const raw = summary[key];
    if (typeof raw === "number") return toRupeesFromWire(raw);
    if (typeof raw === "string" && raw.trim() && !Number.isNaN(Number(raw))) {
      return toRupeesFromWire(Number(raw));
    }
  }
  const value = apiNumber(summary, valueKeys, Number.NaN);
  return Number.isNaN(value) ? fallback : value;
};

const readReportSummaryCount = (
  summary: Record<string, unknown>,
  valueKeys: string[],
  fallback: string | number,
) => {
  const value = apiNumber(summary, valueKeys, Number.NaN);
  return Number.isNaN(value) ? fallback : value;
};

const reportQueryFromFilters = ({
  account,
  method,
  status,
  search,
  searchField,
  dateRange,
}: {
  account: string;
  method: string;
  status: string;
  search?: string;
  searchField?: string;
  dateRange?: PayoutDateRangeValue | null;
}) => ({
  ...(account !== "All Accounts" ? { account } : {}),
  ...(method !== "All Payout Methods" ? { mode: method } : {}),
  ...(status !== "All Status" ? { status } : {}),
  ...(dateRange
    ? { from: toApiDate(dateRange.startDate), to: toApiDate(dateRange.endDate) }
    : {}),
  ...(search?.trim() ? { q: search.trim(), search_by: searchField || "Report Name" } : {}),
});

function downloadReport(name: string, format = "csv") {
  const body = [
    "ClearN Payout Report",
    `Report: ${name}`,
    "Generated from Payout Reports",
  ].join("\n");
  const blob = new Blob([body], { type: "text/plain;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.${format}`;
  link.click();
  URL.revokeObjectURL(url);
}

function SelectBox({
  value,
  options,
  onChange,
  label,
  className = "min-w-[220px]",
}: {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  label?: string;
  className?: string;
}) {
  return (
    <PayoutDropdown
      label={label}
      value={value}
      options={options}
      onChange={onChange}
      className={className}
    />
  );
}

type ReportMetric = {
  label: string;
  value: string | number;
  helper: string;
  icon: React.ReactNode;
  tone: string;
  valueType?: string;
  negative?: boolean;
};

function MetricCard({ stat }: { stat: ReportMetric }) {
  return (
    <div className="flex min-h-[108px] items-center justify-between rounded-[8px] border border-[#E0E5F2] bg-white px-5 py-4 new-shadow">
      <div>
        <p className="text-xs font-semibold text-[#505E8E]">{stat.label}</p>
        <p className="mt-2 text-[20px] font-semibold leading-6 text-[#1B163B]">
          {stat.valueType === "currency" && "\u20B9 "}
          <PayoutKpiValue value={stat.value} />
        </p>
        <p
          className={
            "mt-3 text-xs font-semibold " +
            (stat.negative ? "text-[#7A5C2E]" : "text-[#2F6B49]")
          }
        >
          {stat.negative ? "+ " : "+ "}
          {stat.helper}
        </p>
      </div>
      <span
        className={
          "payout-kpi-icon flex h-10 w-10 items-center justify-center text-lg " +
          stat.tone
        }
      >
        {stat.icon}
      </span>
    </div>
  );
}

function ChartCard({
  title,
  children,
  actions,
}: {
  title: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="flex shrink-0 items-center gap-2 whitespace-nowrap text-base font-semibold text-[#1B163B]">
          {title}
          <FiInfo className="h-4 w-4 shrink-0 text-[#505E8E]" />
        </h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

function PayoutTrendChart() {
  const points = payoutTrend
    .map((item, index) => `${38 + index * 72},${190 - item.value * 4}`)
    .join(" ");

  return (
    <div className="h-[220px]">
      <svg viewBox="0 0 520 220" className="h-full w-full">
        {[40, 80, 120, 160].map((y) => (
          <line
            key={y}
            x1="38"
            x2="490"
            y1={y}
            y2={y}
            stroke="#E0E5F2"
            strokeWidth="1"
          />
        ))}
        <polyline
          points={points}
          fill="none"
          stroke="#5F87D6"
          strokeWidth="3"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {payoutTrend.map((item, index) => (
          <g key={item.label}>
            <circle
              cx={38 + index * 72}
              cy={190 - item.value * 4}
              r="4"
              fill="#FFFFFF"
              stroke="#5F87D6"
              strokeWidth="3"
            />
            <text
              x={38 + index * 72}
              y="214"
              textAnchor="middle"
              fontSize="12"
              fill="#505E8E"
            >
              {item.label}
            </text>
          </g>
        ))}
        {[0, 10, 20, 30, 40].map((label, index) => (
          <text
            key={label}
            x="4"
            y={190 - index * 37}
            fontSize="12"
            fill="#7A8793"
          >
            {label}L
          </text>
        ))}
      </svg>
    </div>
  );
}

function DonutChart({
  total,
  items,
}: {
  total: string;
  items: { label: string; value: string | number; percent: string; color: string }[];
}) {
  const radius = 44;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className="flex min-h-[220px] items-center justify-center gap-8">
      <div className="relative h-[180px] w-[180px]">
        <svg viewBox="0 0 120 120" className="-rotate-90">
          <circle cx="60" cy="60" r={radius} fill="none" stroke="#EEF1F6" strokeWidth="16" />
          {items.map((item) => {
            const numericPercent = Number.parseFloat(item.percent);
            const dash = (numericPercent / 100) * circumference;
            const segment = (
              <circle
                key={item.label}
                cx="60"
                cy="60"
                r={radius}
                fill="none"
                stroke={item.color}
                strokeWidth="16"
                strokeDasharray={`${dash} ${circumference - dash}`}
                strokeDashoffset={-offset}
              />
            );
            offset += dash;
            return segment;
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <span className="text-xs font-semibold text-[#505E8E]">Total</span>
          <span className="text-lg font-semibold text-[#1B163B]">{total}</span>
        </div>
      </div>
      <div className="space-y-4">
        {items.map((item) => (
          <div key={item.label} className="flex items-start gap-3 text-sm">
            <span
              className="mt-1 h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: item.color }}
            />
            <span>
              <span className="block font-semibold text-[#1B163B]">{item.label}</span>
              <span className="block text-xs font-medium text-[#505E8E]">
                {item.value} ({item.percent})
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Heatmap() {
  const intensity = [
    1, 0, 1, 1, 1, 2, 2, 2, 2, 1, 1, 3, 3, 3, 4, 3, 3, 2, 4, 4, 3, 2, 1, 1,
    1, 1, 1, 1, 2, 1, 1,
  ];
  const colors = ["#F6F8FA", "#E8EEF8", "#D6E1F6", "#AFC6F3", "#5F87D6"];

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[1040px]">
        <div className="mb-4 flex items-center justify-between">
          <SelectBox
            value="May 2025"
            options={["May 2025", "April 2025", "March 2025"]}
            onChange={() => undefined}
          />
          <div className="flex items-center gap-2 text-xs font-medium text-[#505E8E]">
            Low
            {[0, 1, 2, 3, 4].map((level) => (
              <span
                key={level}
                className="h-3 w-7 rounded-sm"
                style={{ backgroundColor: colors[level] }}
              />
            ))}
            High
          </div>
        </div>
        <div className="grid grid-cols-[110px_repeat(31,1fr)] items-center gap-2 text-xs font-medium text-[#505E8E]">
          <span />
          {Array.from({ length: 31 }, (_, index) => (
            <span key={index + 1} className="text-center">
              {String(index + 1).padStart(2, "0")}
            </span>
          ))}
          <span>Payout Volume</span>
          {intensity.map((level, index) => (
            <span
              key={index}
              className="h-8 rounded-[2px]"
              style={{ backgroundColor: colors[level] }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function exportInsightsReport() {
  const csv = [
    "Metric,Value",
    "Total Payouts,15678900",
    "Total Transactions,8432",
    "Success Rate,98.72%",
    "Average Payout Amount,18600",
    "Failed Payouts,112",
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "payout-insights-report.csv";
  link.click();
  URL.revokeObjectURL(url);
}

function PayoutReports() {
  const [account, setAccount] = useState("All Accounts");
  const [method, setMethod] = useState("All Payout Methods");
  const [status, setStatus] = useState("All Status");
  const [search, setSearch] = useState("");
  const [searchField, setSearchField] = useState("Report Name");
  const [dateRangeFilter, setDateRangeFilter] =
    useState<PayoutDateRangeValue | null>(null);
  const [pageSize, setPageSize] = useState("10 per page");
  const [currentPage, setCurrentPage] = useState(1);
  const [openReportMenu, setOpenReportMenu] = useState<ReportMenuState | null>(null);
  const [selectedReport, setSelectedReport] = useState<ReportRow | null>(null);
  const [downloadReportModal, setDownloadReportModal] = useState<ReportRow | null>(null);
  const [selectedFormat, setSelectedFormat] = useState("CSV");
  const [renamedReports, setRenamedReports] = useState<Record<string, string>>({});
  const [deletedReports, setDeletedReports] = useState<string[]>([]);
  const rowsPerPage = Number(pageSize.split(" ")[0]) || 10;
  const apiReportQuery = useMemo(
    () =>
      reportQueryFromFilters({
        account,
        method,
        status,
        search,
        searchField,
        dateRange: dateRangeFilter,
      }),
    [account, dateRangeFilter, method, search, searchField, status],
  );
  const reportsSummaryResource = usePayoutResource<Record<string, unknown>>(
    {},
    (signal) => payoutApi.reportsSummary(apiReportQuery, signal),
    [apiReportQuery],
  );
  const reportsResource = usePayoutResource<ReportRow[]>(
    [] as ReportRow[],
    async (signal) => {
      const [catalogRows, jobRows] = await Promise.all([
        payoutApi.reportsCatalog(apiReportQuery, signal),
        payoutApi.reportsJobs(apiReportQuery, signal),
      ]);
      return [...catalogRows, ...jobRows].map((record, index) =>
        reportRowFromApiRecord(record, index),
      );
    },
    [apiReportQuery],
  );
  const reportSourceRows = reportsResource.data;
  const visibleReportStats = useMemo(() => {
    const summary = reportsSummaryResource.data;
    return reportStats.map((stat) => {
      if (stat.label === "Total Payouts") {
        return {
          ...stat,
          value: readReportSummaryMetric(
            summary,
            ["total_payouts_paise", "totalPayoutsPaise"],
            ["total_payouts", "totalPayouts"],
            0,
          ),
        };
      }
      if (stat.label === "Total Transactions") {
        return {
          ...stat,
          value: readReportSummaryCount(summary, ["total_transactions", "totalTransactions", "count"], 0),
        };
      }
      if (stat.label === "Success Rate") {
        const rate = apiNumber(summary, ["success_rate", "successRate"], 0);
        return { ...stat, value: typeof rate === "number" ? `${rate}%` : rate };
      }
      if (stat.label === "Total Fees & Charges") {
        return {
          ...stat,
          value: readReportSummaryMetric(
            summary,
            ["total_fees_paise", "fees_paise", "totalFeesPaise"],
            ["total_fees", "totalFees", "fees"],
            0,
          ),
        };
      }
      if (stat.label === "Failed Payouts") {
        return {
          ...stat,
          value: readReportSummaryCount(summary, ["failed_payouts", "failedPayouts", "failed_count"], 0),
        };
      }
      return stat;
    });
  }, [reportsSummaryResource.data]);
  const activeReports = reportSourceRows
    .filter((row) => !deletedReports.includes(row.id))
    .map((row) => ({
      ...row,
      name: renamedReports[row.id] || row.name,
    }));
  const filteredReports = activeReports.filter((row) => {
    const query = search.trim().toLowerCase();
    const getReportSearchValue = (field: string) => {
      switch (field) {
        case "Description":
          return row.description;
        case "Category":
          return row.category;
        case "Frequency":
          return row.frequency;
        case "Generated By":
          return row.generatedBy;
        case "Report Name":
        default:
          return row.name;
      }
    };
    const matchesAccount = account === "All Accounts" || row.account === account;
    const matchesMethod =
      method === "All Payout Methods" || row.method === method;
    const matchesStatus = status === "All Status" || row.status === status;
    const reportDate = new Date(row.generatedAt);
    const matchesDate =
      !dateRangeFilter ||
      Number.isNaN(reportDate.getTime()) ||
      (reportDate >= dateRangeFilter.startDate &&
        reportDate <= dateRangeFilter.endDate);
    const matchesSearch =
      !query || getReportSearchValue(searchField).toLowerCase().includes(query);

    return matchesAccount && matchesMethod && matchesStatus && matchesDate && matchesSearch;
  });
  const pageCount = Math.max(1, Math.ceil(filteredReports.length / rowsPerPage));
  const visibleReports = filteredReports.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );
  const resultStart = filteredReports.length
    ? (currentPage - 1) * rowsPerPage + 1
    : 0;
  const resultEnd = Math.min(currentPage * rowsPerPage, filteredReports.length);
  const exportReports = () => {
    const headers = [
      "Report Name",
      "Description",
      "Category",
      "Frequency",
      "Last Generated",
      "Generated By",
      "Status",
    ];
    const csvRows = filteredReports.map((row) =>
      [
        row.name,
        row.description,
        row.category,
        row.frequency,
        row.generatedAt,
        row.generatedBy,
        row.status,
      ]
        .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
        .join(","),
    );
    const blob = new Blob([[headers.join(","), ...csvRows].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "payout-reports.csv";
    link.click();
    URL.revokeObjectURL(url);
  };
  const createCustomReport = async () => {
    if (!isPayoutApiMode()) {
      downloadReport("Custom Payout Report", "csv");
      return;
    }
    try {
      await payoutApi.createReportJob(
        buildReportJobBody({ name: "Custom Payout Report" }, "CSV", {
          account,
          method,
          status,
          dateRange: dateRangeFilter,
        }),
        createPayoutIdempotencyKey("report-job", "custom-payout-report"),
      );
      reportsResource.refresh();
      showPayoutNotification({
        tone: "success",
        message: "Report generation started.",
      });
    } catch (error) {
      showPayoutNotification({
        tone: "error",
        message: error instanceof Error ? error.message : "Unable to generate report.",
      });
    }
  };
  const viewReport = (row: ReportRow) => {
    setSelectedReport(row);
    setOpenReportMenu(null);
  };
  const renameReport = async (row: ReportRow) => {
    const nextName = window.prompt("Rename report", row.name);
    if (!nextName?.trim()) {
      setOpenReportMenu(null);
      return;
    }
    if (!isPayoutApiMode()) {
      setRenamedReports((current) => ({
        ...current,
        [row.id]: nextName.trim(),
      }));
      setOpenReportMenu(null);
      return;
    }
    if (!row.jobId) {
      showPayoutNotification({
        tone: "warning",
        message: "Catalog report templates cannot be renamed.",
      });
      setOpenReportMenu(null);
      return;
    }
    try {
      await payoutApi.updateReportJob(
        row.jobId,
        { name: nextName.trim() },
        createPayoutIdempotencyKey("report-rename", row.jobId),
      );
      reportsResource.refresh();
    } catch (error) {
      showPayoutNotification({
        tone: "error",
        message: error instanceof Error ? error.message : "Unable to rename report.",
      });
    }
    setOpenReportMenu(null);
  };
  const deleteReport = async (row: ReportRow) => {
    if (!window.confirm(`Delete ${row.name}? This report job will no longer be available.`)) {
      setOpenReportMenu(null);
      return;
    }
    if (!isPayoutApiMode()) {
      setDeletedReports((current) => [...current, row.id]);
      setOpenReportMenu(null);
      setCurrentPage(1);
      return;
    }
    if (!row.jobId) {
      showPayoutNotification({
        tone: "warning",
        message: "Catalog report templates cannot be deleted.",
      });
      setOpenReportMenu(null);
      return;
    }
    try {
      await payoutApi.deleteReportJob(
        row.jobId,
        createPayoutIdempotencyKey("report-delete", row.jobId),
      );
      reportsResource.refresh();
    } catch (error) {
      showPayoutNotification({
        tone: "error",
        message: error instanceof Error ? error.message : "Unable to delete report.",
      });
      setOpenReportMenu(null);
      return;
    }
    setDeletedReports((current) => [...current, row.id]);
    setOpenReportMenu(null);
    setCurrentPage(1);
  };
  const openDownloadModal = (row: ReportRow) => {
    setSelectedFormat("CSV");
    setDownloadReportModal(row);
  };
  const confirmReportDownload = async () => {
    if (!downloadReportModal) {
      return;
    }
    if (!isPayoutApiMode()) {
      downloadReport(downloadReportModal.name, selectedFormat.toLowerCase());
      setDownloadReportModal(null);
      return;
    }
    try {
      let downloadUrl = downloadReportModal.downloadUrl || "";
      if (downloadReportModal.jobId) {
        const response = await payoutApi.reportDownload(downloadReportModal.jobId, {
          format: reportFormatForApi(selectedFormat),
        });
        downloadUrl = readReportDownloadUrl(response);
      } else {
        const response = await payoutApi.createReportJob(
          buildReportJobBody(downloadReportModal, selectedFormat, {
            account,
            method,
            status,
            dateRange: dateRangeFilter,
          }),
          createPayoutIdempotencyKey("report-download", downloadReportModal.id),
        );
        downloadUrl = readReportDownloadUrl(response);
      }
      if (downloadUrl) {
        window.open(downloadUrl, "_blank", "noopener,noreferrer");
      } else {
        reportsResource.refresh();
        showPayoutNotification({
          tone: "success",
          message: "Report generation started. Download will be available once it is ready.",
        });
      }
      setDownloadReportModal(null);
    } catch (error) {
      showPayoutNotification({
        tone: "error",
        message: error instanceof Error ? error.message : "Unable to download report.",
      });
    }
  };
  const clearFilters = () => {
    setAccount("All Accounts");
    setMethod("All Payout Methods");
    setStatus("All Status");
    setSearch("");
    setSearchField("Report Name");
    setDateRangeFilter(null);
    setCurrentPage(1);
  };
  const toggleReportMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    row: ReportRow,
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setOpenReportMenu((current) =>
      current?.row.id === row.id
        ? null
        : {
            row,
            top: rect.bottom + 6,
            left: Math.max(12, rect.right - 160),
          },
    );
  };
  const tableRows = visibleReports.map((row) => [
    <span className="flex items-center gap-4">
      <span
        className={
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-[6px] text-base " +
          row.tone
        }
      >
        {row.icon}
      </span>
      <PayoutTableCell
        value={row.name}
        valueClassName="font-semibold text-[#1B163B]"
      />
    </span>,
    <PayoutTableCell
      value={row.description}
      valueClassName="text-sm font-medium text-[#1B163B]"
      maxWidthClassName="max-w-[310px]"
    />,
    <span
      className={
        "rounded-[5px] px-2 py-1 text-xs font-semibold " + row.tone
      }
    >
      {row.category}
    </span>,
    row.frequency,
    row.generatedAt,
    <PayoutTableCell
      value={row.generatedBy}
      valueClassName="font-medium text-[#1B163B]"
    />,
    <span className="relative flex items-center gap-4">
      <button
        type="button"
        onClick={() => openDownloadModal(row)}
        className="flex h-9 w-9 items-center justify-center rounded-[6px] border border-[#E0E5F2] bg-white text-[#1B163B]"
      >
        <FiDownload />
      </button>
      <span>
        <button
          type="button"
          onClick={(event) => toggleReportMenu(event, row)}
          className="flex h-9 w-9 items-center justify-center rounded-[6px] text-[#1B163B]"
        >
          <FiMoreVertical />
        </button>
      </span>
    </span>,
  ]);

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="mb-8 text-sm font-medium text-[#505E8E]">
            Reports <span className="mx-2">{">"}</span> Payout Reports
          </div>
          <h1 className="text-[24px] font-semibold leading-8 text-[#1B163B]">
            Payout Reports
          </h1>
          <p className="mt-1 text-sm font-medium text-[#505E8E]">
            View, generate and download reports related to your payout operations.
          </p>
        </div>
        <button
          type="button"
          onClick={createCustomReport}
          style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
          className="mt-8 flex h-10 items-center gap-3 rounded-[6px] bg-edviron_black px-5 text-sm font-semibold text-white shadow-sm hover:bg-edviron_black/90"
        >
          <FiPlus />
          Generate Custom Report
        </button>
      </div>

      <section className="payout-filter-card payout-filter-grid payout-filter-grid--with-search mb-5 rounded-[8px] border border-[#E0E5F2] bg-white px-5 py-5 new-shadow">
        <PayoutSearchFilter
          value={search}
          onChange={(value) => {
            setSearch(value);
            setCurrentPage(1);
          }}
          filterBy={searchField}
          onFilterByChange={(value) => {
            setSearchField(value);
            setCurrentPage(1);
          }}
          filterOptions={[
            "Report Name",
            "Description",
            "Category",
            "Frequency",
            "Generated By",
          ]}
          placeholder={`Search by ${searchField}`}
          className="w-full lg:col-span-2"
        />
        <PayoutDateRangeFilter
          label="Date Range"
          displayText={
            dateRangeFilter ? formatPayoutDateRangeLabel(dateRangeFilter) : "Date (all)"
          }
          displayTextMode="override"
          onChange={(range) => {
            setDateRangeFilter(range);
            setCurrentPage(1);
          }}
          className="w-full min-w-0"
        />
        <SelectBox
          label="Account"
          value={account}
          options={["All Accounts", "Operating Account", "Salary Account", "Vendor Account"]}
          onChange={(value) => {
            setAccount(value);
            setCurrentPage(1);
          }}
        />
        <SelectBox
          label="Payout Method"
          value={method}
          options={["All Payout Methods", "NEFT", "IMPS", "RTGS"]}
          onChange={(value) => {
            setMethod(value);
            setCurrentPage(1);
          }}
        />
        <SelectBox
          label="Status"
          value={status}
          options={["All Status", "Active", "Failed"]}
          onChange={(value) => {
            setStatus(value);
            setCurrentPage(1);
          }}
        />
        <PayoutFilterActions
          onClear={clearFilters}
          onExport={exportReports}
          className="w-full"
        />
        <PayoutFilterChips
          className="col-span-full"
          onClearAll={clearFilters}
          chips={[
            {
              label: "Date Range",
              value: dateRangeFilter
                ? formatPayoutDateRangeLabel(dateRangeFilter)
                : "Date (all)",
              hidden: !dateRangeFilter,
              onRemove: () => {
                setDateRangeFilter(null);
                setCurrentPage(1);
              },
            },
            {
              label: "Account",
              value: account,
              onRemove: () => {
                setAccount("All Accounts");
                setCurrentPage(1);
              },
            },
            {
              label: "Payout Method",
              value: method,
              onRemove: () => {
                setMethod("All Payout Methods");
                setCurrentPage(1);
              },
            },
            {
              label: "Status",
              value: status,
              onRemove: () => {
                setStatus("All Status");
                setCurrentPage(1);
              },
            },
            {
              label: "Search",
              value: search,
              onRemove: () => {
                setSearch("");
                setCurrentPage(1);
              },
            },
          ]}
        />
      </section>

      <div className="mb-5 grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(190px,1fr))]">
        {visibleReportStats.map((stat) => (
          <MetricCard key={stat.label} stat={stat} />
        ))}
      </div>

      <PayoutDataTable
        title="All Reports"
        className="mb-5"
        minWidth="min-w-[1280px]"
        columns={[
          "Report Name",
          "Description",
          "Category",
          "Frequency",
          "Last Generated",
          "Generated By",
          "Actions",
        ]}
        rows={tableRows}
        footer={
          <div className="flex min-h-[64px] w-full items-center justify-between gap-4 border-t border-[#E0E5F2] px-5 py-4 text-xs font-medium text-[#505E8E]">
            <span className="min-w-max">
              Showing {resultStart} to {resultEnd} of {filteredReports.length} reports
            </span>
            <div className="ml-auto flex min-w-max items-center gap-4">
              <SelectBox
                value={pageSize}
                options={["10 per page", "20 per page", "50 per page"]}
                onChange={(value) => {
                  setPageSize(value);
                  setCurrentPage(1);
                }}
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  className="flex h-9 w-9 items-center justify-center rounded-[6px] border border-[#E0E5F2] disabled:opacity-50"
                >
                  {"<"}
                </button>
                {[1, 2, 3].filter((page) => page <= pageCount).map((page) => (
                  <button
                    type="button"
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={
                      "h-9 w-9 rounded-[6px] border " +
                      (currentPage === page
                        ? "border-[#6687FF] bg-[#EEF1F6] text-[#6687FF]"
                        : "border-[#E0E5F2] text-[#1B163B]")
                    }
                  >
                    {page}
                  </button>
                ))}
                {pageCount > 3 && (
                  <>
                    <span className="px-2 text-[#505E8E]">...</span>
                    <button
                      type="button"
                      onClick={() => setCurrentPage(pageCount)}
                      className="h-9 w-9 rounded-[6px] border border-[#E0E5F2] text-[#1B163B]"
                    >
                      {pageCount}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  disabled={currentPage === pageCount}
                  onClick={() => setCurrentPage((page) => Math.min(pageCount, page + 1))}
                  className="flex h-9 w-9 items-center justify-center rounded-[6px] border border-[#E0E5F2] disabled:opacity-50"
                >
                  {">"}
                </button>
              </div>
            </div>
          </div>
        }
      />

      {openReportMenu && (
        <div
          className="fixed z-[300] w-40 overflow-hidden rounded-[8px] border border-[#E0E5F2] bg-white py-2 text-sm font-semibold text-[#1B163B] new-shadow"
          style={{ top: openReportMenu.top, left: openReportMenu.left }}
        >
          <button
            type="button"
            onClick={() => viewReport(openReportMenu.row)}
            className="block w-full px-4 py-2 text-left hover:bg-[#F6F8FA]"
          >
            View Report
          </button>
          <button
            type="button"
            onClick={() => renameReport(openReportMenu.row)}
            className="block w-full px-4 py-2 text-left hover:bg-[#F6F8FA]"
          >
            Rename
          </button>
          <button
            type="button"
            onClick={() => deleteReport(openReportMenu.row)}
            className="block w-full px-4 py-2 text-left text-[#7D3A3A] hover:bg-[#F6F8FA]"
          >
            Delete
          </button>
        </div>
      )}

      {downloadReportModal && (
        <div className="fixed inset-0 z-[250] flex items-center justify-center bg-[#1B163B]/30 px-4">
          <section className="w-full max-w-[460px] rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
            <div className="mb-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#505E8E]">
                Download Report
              </p>
              <h2 className="mt-2 text-xl font-semibold text-[#1B163B]">
                {downloadReportModal.name}
              </h2>
              <p className="mt-1 text-sm font-medium text-[#505E8E]">
                Select the file format you want to download.
              </p>
            </div>
            <SelectBox
              label="Report Format"
              value={selectedFormat}
              options={["CSV", "XLS", "PDF"]}
              onChange={setSelectedFormat}
              className="w-full"
            />
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDownloadReportModal(null)}
                className="h-10 rounded-[6px] border border-[#E0E5F2] bg-white px-5 text-sm font-semibold text-[#1B163B]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmReportDownload}
                style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
                className="flex h-10 items-center gap-2 rounded-[6px] px-5 text-sm font-semibold"
              >
                <FiDownload />
                Download
              </button>
            </div>
          </section>
        </div>
      )}

      {selectedReport && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-[#1B163B]/30 px-4">
          <section className="w-full max-w-[560px] rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[#505E8E]">
                  Report Preview
                </p>
                <h2 className="mt-2 text-xl font-semibold text-[#1B163B]">
                  {selectedReport.name}
                </h2>
                <p className="mt-1 text-sm font-medium text-[#505E8E]">
                  {selectedReport.description}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedReport(null)}
                className="flex h-9 w-9 items-center justify-center rounded-[6px] border border-[#E0E5F2] text-[#1B163B]"
              >
                x
              </button>
            </div>
            <div className="grid gap-3 rounded-[8px] bg-[#F6F8FA] p-4 text-sm font-medium text-[#1B163B]">
              <div className="flex justify-between gap-4">
                <span className="text-[#505E8E]">Category</span>
                <span>{selectedReport.category}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-[#505E8E]">Frequency</span>
                <span>{selectedReport.frequency}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-[#505E8E]">Last Generated</span>
                <span>{selectedReport.generatedAt}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-[#505E8E]">Generated By</span>
                <span>{selectedReport.generatedBy}</span>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setSelectedReport(null)}
                className="h-10 rounded-[6px] border border-[#E0E5F2] bg-white px-5 text-sm font-semibold text-[#1B163B]"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => downloadReport(selectedReport.name, "csv")}
                style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
                className="flex h-10 items-center gap-2 rounded-[6px] px-5 text-sm font-semibold"
              >
                <FiDownload />
                Download
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

export function PayoutInsights() {
  const [account, setAccount] = useState("All Accounts");
  const [method, setMethod] = useState("All Payout Methods");
  const [dateRangeFilter, setDateRangeFilter] =
    useState<PayoutDateRangeValue | null>(null);
  const clearFilters = () => {
    setAccount("All Accounts");
    setMethod("All Payout Methods");
    setDateRangeFilter(null);
  };

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-8 text-sm font-medium text-[#505E8E]">
        Reports <span className="mx-2">{">"}</span> Payout Insights
      </div>

      <section className="payout-filter-card mb-5 flex flex-wrap items-end gap-4">
        <div className="mr-auto min-w-[280px]">
          <h1 className="text-[24px] font-semibold leading-8 text-[#1B163B]">
            Payout Advance Insights
          </h1>
          <p className="mt-1 text-sm font-medium text-[#505E8E]">
            Gain deeper insights into your payout operations and performance.
          </p>
        </div>
        <div className="w-[220px] max-w-full">
          <PayoutDateRangeFilter
            label="Date Range"
            displayText={
              dateRangeFilter ? formatPayoutDateRangeLabel(dateRangeFilter) : "Date (all)"
            }
            displayTextMode="override"
            onChange={setDateRangeFilter}
          />
        </div>
        <SelectBox
          label="Account"
          value={account}
          options={["All Accounts", "Operating Account", "Salary Account", "Vendor Account"]}
          onChange={setAccount}
          className="min-w-[150px]"
        />
        <SelectBox
          label="Payout Method"
          value={method}
          options={["All Payout Methods", "NEFT", "IMPS", "RTGS"]}
          onChange={setMethod}
          className="min-w-[170px]"
        />
        <PayoutFilterActions
          onClear={clearFilters}
          onExport={exportInsightsReport}
          exportLabel="Export"
        />
        <PayoutFilterChips
          className="basis-full"
          onClearAll={clearFilters}
          chips={[
            {
              label: "Date Range",
              value: dateRangeFilter
                ? formatPayoutDateRangeLabel(dateRangeFilter)
                : "Date (all)",
              hidden: !dateRangeFilter,
              onRemove: () => setDateRangeFilter(null),
            },
            {
              label: "Account",
              value: account,
              onRemove: () => setAccount("All Accounts"),
            },
            {
              label: "Payout Method",
              value: method,
              onRemove: () => setMethod("All Payout Methods"),
            },
          ]}
        />
      </section>

      <div className="mb-5 grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(190px,1fr))]">
        {insightStats.map((stat) => (
          <MetricCard key={stat.label} stat={stat} />
        ))}
      </div>

      <div className="mb-5 grid gap-4 xl:grid-cols-[1.2fr_1fr_1.2fr]">
        <ChartCard
          title="Trend"
          actions={
            <div className="flex gap-2">
              <SelectBox
                value="Amount"
                options={["Amount", "Transactions"]}
                onChange={() => undefined}
                className="min-w-[110px]"
              />
              <SelectBox
                value="Daily"
                options={["Daily", "Weekly", "Monthly"]}
                onChange={() => undefined}
                className="min-w-[104px]"
              />
            </div>
          }
        >
          <div className="mb-2 flex items-center gap-2 text-xs font-medium text-[#505E8E]">
            <span className="h-2 w-2 rounded-full bg-[#5F87D6]" />
            {"Amount (\u20B9)"}
          </div>
          <PayoutTrendChart />
        </ChartCard>

        <ChartCard title="By Status">
          <DonutChart total="8,432" items={statusBreakdown} />
        </ChartCard>

        <ChartCard
          title="By Methods"
          actions={
            <SelectBox
              value="Amount"
              options={["Amount", "Transactions"]}
              onChange={() => undefined}
              className="min-w-[110px]"
            />
          }
        >
          <DonutChart total="" items={methodBreakdown} />
        </ChartCard>
      </div>

      <div className="mb-5 grid gap-4 xl:grid-cols-2">
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
          <h2 className="mb-4 flex items-center gap-2 text-base font-semibold text-[#1B163B]">
            Top Beneficiaries by Payout Amount
            <FiInfo className="text-sm text-[#505E8E]" />
          </h2>
          <PayoutDataTable
            minWidth="min-w-[620px]"
            columns={["Beneficiary", "Amount (\u20B9)", "Transactions", "Success Rate"]}
            rows={topBeneficiaries.map((row) => row)}
          />
          <button className="mt-3 flex items-center gap-2 text-sm font-semibold text-[#2563EB]">
            View All Beneficiaries <FaArrowRight className="text-xs" />
          </button>
        </section>

        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
          <h2 className="mb-4 flex items-center gap-2 text-base font-semibold text-[#1B163B]">
            Failed Payouts Analysis
            <FiInfo className="text-sm text-[#505E8E]" />
          </h2>
          <PayoutDataTable
            minWidth="min-w-[620px]"
            columns={["Reason", "Transactions", "Amount (\u20B9)", "% of Failures"]}
            rows={failureAnalysis.map((row) => row)}
          />
          <button className="mt-3 flex items-center gap-2 text-sm font-semibold text-[#2563EB]">
            View All Failures <FaArrowRight className="text-xs" />
          </button>
        </section>
      </div>

      <ChartCard title="Payout Volume Heatmap">
        <Heatmap />
      </ChartCard>
    </div>
  );
}

export default PayoutReports;
